import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { RotateCcw, Clock, Package, AlertCircle, CheckCircle, ChevronRight } from 'lucide-react';

// Breadcrumb schema for SEO
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.edenremedies.com"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Returns & Refunds Policy",
      "item": "https://www.edenremedies.com/returns-policy"
    }
  ]
};

const ReturnsPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#f5f1eb] py-12">
      {/* Breadcrumb Schema for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-[#2d4a47] transition-colors">Home</Link>
          <ChevronRight size={14} />
          <span className="text-gray-900">Returns & Refunds</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-8">Returns & Refunds Policy</h1>
        
        <div className="bg-white p-8 rounded-lg shadow-sm space-y-8">
          {/* Overview */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <RotateCcw className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Our Return Policy</h2>
            </div>
            <div className="space-y-3 text-gray-600">
              <p>We want you to be completely satisfied with your Eden Remedies purchase. If for any reason you're not happy with your order, we offer a 30-day return policy from the date of delivery.</p>
            </div>
          </section>

          {/* Return Window */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Clock className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Return Window</h2>
            </div>
            <div className="space-y-3 text-gray-600">
              <p><strong>30 Days:</strong> You have 30 days from the date of delivery to initiate a return.</p>
              <p>Items must be returned in their original packaging and in unused condition to qualify for a full refund.</p>
            </div>
          </section>

          {/* Eligible Items */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Eligible for Return</h2>
            </div>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                <span>Unopened products in original, sealed packaging</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                <span>Products received damaged or defective</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                <span>Incorrect items received</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                <span>Products that caused an allergic reaction (with documentation)</span>
              </li>
            </ul>
          </section>

          {/* Non-Eligible Items */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">Not Eligible for Return</h2>
            </div>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 mt-1 flex-shrink-0" />
                <span>Opened or used products (for hygiene reasons)</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 mt-1 flex-shrink-0" />
                <span>Products without original packaging</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 mt-1 flex-shrink-0" />
                <span>Items returned after 30 days</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 mt-1 flex-shrink-0" />
                <span>Gift cards or promotional items</span>
              </li>
            </ul>
          </section>

          {/* How to Return */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Package className="text-[#2d4a47]" size={24} />
              <h2 className="text-xl font-medium text-gray-900">How to Initiate a Return</h2>
            </div>
            <ol className="space-y-4 text-gray-600">
              <li className="flex gap-3">
                <span className="bg-[#2d4a47] text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-sm">1</span>
                <span><strong>Contact Us:</strong> Email us at returns@edenremedies.com with your order number and reason for return.</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-[#2d4a47] text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-sm">2</span>
                <span><strong>Receive Authorization:</strong> We'll review your request and send you a Return Merchandise Authorization (RMA) number within 2 business days.</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-[#2d4a47] text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-sm">3</span>
                <span><strong>Ship Your Return:</strong> Pack the item securely in its original packaging and include your RMA number. Ship to the address provided in your authorization email.</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-[#2d4a47] text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-sm">4</span>
                <span><strong>Receive Your Refund:</strong> Once we receive and inspect your return, we'll process your refund within 5-7 business days.</span>
              </li>
            </ol>
          </section>

          {/* Refund Information */}
          <section className="bg-[#f5f1eb] p-6 rounded-lg">
            <h2 className="text-xl font-medium text-gray-900 mb-4">Refund Information</h2>
            <div className="space-y-3 text-gray-600">
              <p><strong>Refund Method:</strong> Refunds will be issued to the original payment method used for the purchase.</p>
              <p><strong>Processing Time:</strong> Please allow 5-7 business days for the refund to appear on your statement after we process it.</p>
              <p><strong>Shipping Costs:</strong> Original shipping costs are non-refundable unless the return is due to our error (damaged, defective, or incorrect item).</p>
              <p><strong>Return Shipping:</strong> Customers are responsible for return shipping costs unless the return is due to our error.</p>
            </div>
          </section>

          {/* Damaged Items */}
          <section>
            <h2 className="text-xl font-medium text-gray-900 mb-4">Damaged or Defective Items</h2>
            <div className="space-y-3 text-gray-600">
              <p>If you receive a damaged or defective item, please contact us within 48 hours of delivery with photos of the damage. We will arrange for a replacement or full refund, including shipping costs.</p>
            </div>
          </section>

          {/* Exchanges */}
          <section>
            <h2 className="text-xl font-medium text-gray-900 mb-4">Exchanges</h2>
            <div className="space-y-3 text-gray-600">
              <p>We do not offer direct exchanges. If you'd like a different product or size, please return your original purchase for a refund and place a new order.</p>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t pt-6">
            <p className="text-gray-600">
              Questions about returns? Contact our customer service team at{' '}
              <a href="mailto:returns@edenremedies.com" className="text-[#2d4a47] underline">
                returns@edenremedies.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/products" className="text-[#2d4a47] underline hover:opacity-70">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPolicyPage;
